import { createBrowserRouter } from 'react-router';
import ProtectedRoute from '../components/ProtectedRoute';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import ProfilePage from '../pages/admin/Account/Profile';
import AdminMainPage from '../pages/admin/AdminMain';
import AdminWrapperMainLayout from '../pages/admin/AdminWrapperMainLayout';
import AnalyticsPage from '../pages/admin/Analytics';
import ContractsPage from '../pages/admin/Contracts';
import AssignedVesselsPage from '../pages/admin/Contracts/AssignedVessels';
import ContractTemplate, {
  ContractSubPageWrapper,
} from '../pages/admin/Contracts/ContractTemplate';
import CreateContractPage from '../pages/admin/Contracts/Create';
import EditContractPage from '../pages/admin/Contracts/Edit';
import ContractInsurancesPage from '../pages/admin/Contracts/Insurances';
import { ContractTimetablePage } from '../pages/admin/Contracts/Timetable';
import CrewPage from '../pages/admin/Crew';
import CrewCertificationsPage from '../pages/admin/Crew/Certifications';
import CreateCrewPage from '../pages/admin/Crew/Create';
import CrewTemplate, {
  CrewSubPageWrapper,
} from '../pages/admin/Crew/CrewTemplate';
import EditCrewPage from '../pages/admin/Crew/Edit';
import InvoicesPage from '../pages/admin/Invoices';
import InvoiceTabPage from '../pages/admin/Invoices/InvoiceTabPage';
import InvoiceTemplate, {
  InvoiceSubPageWrapper,
} from '../pages/admin/Invoices/InvoiceTemplate';
import InvoiceJourneysPage from '../pages/admin/Invoices/Journeys';
import InvoiceRejectionNotesPage from '../pages/admin/Invoices/RejectionNotes';
import IslandsPage from '../pages/admin/Islands';
import CreateIslandPage from '../pages/admin/Islands/Create';
import EditIslandPage from '../pages/admin/Islands/Edit';
import JourneysPage from '../pages/admin/Journeys';
import CreateJourneyPage from '../pages/admin/Journeys/Create';
import EditJourneyPage from '../pages/admin/Journeys/Edit';
import JourneyLogPage from '../pages/admin/Journeys/JourneyLog';
import JourneyTemplate, {
  JourneySubPageWrapper,
} from '../pages/admin/Journeys/JourneyTemplate';
import JourneyNotesPage from '../pages/admin/Journeys/Notes';
import OperatorsPage from '../pages/admin/Operators';
import CreateOperatorPage from '../pages/admin/Operators/Create';
import EditOperatorPage from '../pages/admin/Operators/Edit';
import UsersPage from '../pages/admin/Users';
import CreateUserPage from '../pages/admin/Users/Create';
import EditUserPage from '../pages/admin/Users/Edit';
import VesselsPage from '../pages/admin/Vessels';
import VesselAssignmentsPage from '../pages/admin/Vessels/Assignments';
import VesselCertificationsPage from '../pages/admin/Vessels/Certifications';
import CreateVesselPage from '../pages/admin/Vessels/Create';
import EditVesselPage from '../pages/admin/Vessels/Edit';
import VesselInsurancesPage from '../pages/admin/Vessels/Insurances';
import VesselTemplate, {
  VesselSubPageWrapper,
} from '../pages/admin/Vessels/VesselTemplate';
import NotFoundPage from '../pages/error/NotFoundPage';
import UnauthorizedPage from '../pages/error/UnauthorizedPage';
import LogoutPage from '../pages/Logout';
import IslandFerryTimetablePage from '../pages/public/IslandFerryTimetable';
import MainPage from '../pages/public/Main';
import MainLayout from '../pages/public/MainLayout';
import { Role } from '../types/user';
import OperatorTemplate, {
  OperatorSubPageWrapper,
} from '../pages/admin/Operators/OperatorTemplate';
import UserTemplate, {
  UserSubPageWrapper,
} from '../pages/admin/Users/UserTemplate';
import IslandTemplate, {
  IslandSubPageWrapper,
} from '../pages/admin/Islands/IslandTemplate';

export const router = createBrowserRouter([
  {
    path: '',
    element: <MainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <MainPage /> },
      { path: 'islands/:islandId', element: <IslandFerryTimetablePage /> },
    ],
  },
  {
    path: 'admin',
    element: <AdminWrapperMainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <AdminMainPage /> },
      {
        path: 'contracts',
        children: [
          { index: true, element: <ContractsPage /> },
          {
            element: <ContractTemplate />,
            children: [
              { path: 'create', element: <CreateContractPage /> },
              {
                element: <ContractSubPageWrapper />,
                children: [
                  { path: ':contractId', element: <EditContractPage /> },
                  {
                    path: ':contractId/timetable',
                    element: <ContractTimetablePage />,
                  },
                  {
                    path: ':contractId/insurance',
                    element: <ContractInsurancesPage />,
                  },
                  {
                    path: ':contractId/vessels',
                    element: <AssignedVesselsPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'invoices',
        children: [
          { index: true, element: <InvoicesPage /> },
          {
            element: <InvoiceTemplate />,
            children: [
              {
                element: <InvoiceSubPageWrapper />,
                children: [
                  { path: ':invoiceId', element: <InvoiceJourneysPage /> },
                  { path: ':invoiceId/invoice', element: <InvoiceTabPage /> },
                  {
                    path: ':invoiceId/rejection-notes',
                    element: <InvoiceRejectionNotesPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'crew',
        children: [
          { index: true, element: <CrewPage /> },
          {
            element: <CrewTemplate />,
            children: [
              { path: 'create', element: <CreateCrewPage /> },
              {
                element: <CrewSubPageWrapper />,
                children: [
                  {
                    path: ':crewMemberId',
                    element: <EditCrewPage />,
                  },
                  {
                    path: ':crewMemberId/certifications',
                    element: <CrewCertificationsPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'islands',
        element: <ProtectedRoute requiredRoles={[Role.ADMIN]} />,
        children: [
          { index: true, element: <IslandsPage /> },
          {
            element: <IslandTemplate />,
            children: [
              { path: 'create', element: <CreateIslandPage /> },
              {
                element: <IslandSubPageWrapper />,
                children: [{ path: ':islandId', element: <EditIslandPage /> }],
              },
            ],
          },
        ],
      },
      {
        path: 'operators',
        element: <ProtectedRoute requiredRoles={[Role.ADMIN, Role.DRCD]} />,
        children: [
          { index: true, element: <OperatorsPage /> },
          {
            element: <OperatorTemplate />,
            children: [
              { path: 'create', element: <CreateOperatorPage /> },
              {
                element: <OperatorSubPageWrapper />,
                children: [
                  { path: ':operatorId', element: <EditOperatorPage /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'users',
        element: <ProtectedRoute requiredRoles={[Role.ADMIN]} />,
        children: [
          { index: true, element: <UsersPage /> },
          {
            element: <UserTemplate />,
            children: [
              { path: 'create', element: <CreateUserPage /> },
              {
                element: <UserSubPageWrapper />,
                children: [{ path: ':userId', element: <EditUserPage /> }],
              },
            ],
          },
        ],
      },
      {
        path: 'vessels',
        children: [
          { index: true, element: <VesselsPage /> },
          {
            element: <VesselTemplate />,
            children: [
              { path: 'create', element: <CreateVesselPage /> },
              {
                element: <VesselSubPageWrapper />,
                children: [
                  { path: ':vesselId', element: <EditVesselPage /> },
                  {
                    path: ':vesselId/certifications',
                    element: <VesselCertificationsPage />,
                  },
                  {
                    path: ':vesselId/insurance',
                    element: <VesselInsurancesPage />,
                  },
                  {
                    path: ':vesselId/contracts',
                    element: <VesselAssignmentsPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'journeys',
        children: [
          {
            index: true,
            element: <JourneysPage />,
          },
          {
            element: <JourneyTemplate />,
            children: [
              { path: 'create', element: <CreateJourneyPage /> },
              {
                element: <JourneySubPageWrapper />,
                children: [
                  { path: ':journeyId', element: <EditJourneyPage /> },
                  {
                    path: ':journeyId/log',
                    element: <JourneyLogPage />,
                  },
                  {
                    path: ':journeyId/notes',
                    element: <JourneyNotesPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'analytics',
        element: (
          <ProtectedRoute requiredRoles={[Role.ADMIN, Role.DRCD]}>
            <AnalyticsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'account',
        children: [{ path: 'profile', element: <ProfilePage /> }],
      },
    ],
  },
  { path: '/logout', element: <LogoutPage /> },
  { path: '/unauthorized', element: <UnauthorizedPage /> },
  { path: '/*', element: <NotFoundPage /> },
]);
